import {
  ClientMessage,
  ServerMessage,
  SessionState,
  SessionInfo,
} from '../types/network';
import { PacketHandler } from './PacketHandler';
import { MessageQueue } from './MessageQueue';
import { networkEvents } from '../core/EventEmitter';
import { ServerConfig } from '../config';

/**
 * ConnectionManager - WebSocket connection lifecycle management.
 *
 * Responsibilities:
 * - WebSocket connection/Disconnection
 * - Auto-reconnection with exponential backoff
 * - Message serialization/deserialization
 * - Session state tracking
 * - Ping/Pong heartbeat
 */
export class ConnectionManager {
  private static instance: ConnectionManager;

  private ws: WebSocket | null = null;
  private gatewayUrl: string;
  private packetHandler: PacketHandler;
  private messageQueue: MessageQueue;

  // Reconnection settings
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private baseReconnectDelay: number = 1000; // ms
  private reconnectTimer: number | null = null;

  /** disconnect()에 의한 의도적 종료 여부 — true면 onclose에서 재연결하지 않는다. */
  private intentionalClose: boolean = false;

  /** 진행 중인 connect() Promise — CONNECTING 중 재진입 시 재사용. */
  private connectPromise: Promise<void> | null = null;

  /**
   * 현재 소켓의 종료 처리(상태 변경/이벤트/재연결)가 이미 수행됐는지.
   * onclose 경로와 pong 타임아웃의 직접 처리 경로가 겹쳐도 한 번만 실행되게 한다.
   */
  private closeHandled: boolean = false;

  // Heartbeat settings
  private pingInterval: number = ServerConfig.PING_INTERVAL; // ms
  private pongTimeout: number = ServerConfig.PONG_TIMEOUT; // ms
  private pingTimer: number | null = null;
  private lastPongTime: number = 0;

  /** 직전 heartbeat tick이 실제로 실행된 시각 — 타이머 지연(오탐) 감지에 쓴다. */
  private lastTickTime: number = 0;

  /** 이번 heartbeat 사이클에서 타임아웃을 이미 처리했는지 — 경고 폭주/재진입 방지. */
  private pongTimeoutHandled: boolean = false;

  /** visibilitychange 리스너 참조 — stopHeartbeat에서 반드시 해제한다(누수 방지). */
  private visibilityHandler: (() => void) | null = null;

  /**
   * 타이머 지연 허용 배수.
   * 탭이 백그라운드로 가거나 메인 스레드가 오래 블록되면 setInterval이 밀리는데,
   * 풀리는 순간 lastPongTime과의 차이가 pongTimeout을 넘어 멀쩡한 연결을 끊는 오탐이 난다.
   * 실제 tick 간격이 pingInterval의 이 배수를 넘으면 "쉬었다 온 tick"으로 보고
   * 그 tick의 타임아웃 판정을 건너뛴다.
   */
  private static readonly TICK_DELAY_TOLERANCE = 2;

  // Session state
  private session: SessionInfo = {
    state: SessionState.DISCONNECTED,
    account: null,
    charName: null,
  };

  // Event listeners
  private listeners: Map<string, Set<(data: unknown) => void>> = new Map();

  private constructor(gatewayUrl: string = ServerConfig.GATEWAY_URL) {
    this.gatewayUrl = gatewayUrl;
    this.packetHandler = new PacketHandler(this.sendRaw.bind(this));
    // 큐의 발송 함수는 반드시 sendRaw여야 한다.
    // send에 연결하면 미연결 시 send가 다시 enqueue하는 순환이 생긴다.
    this.messageQueue = new MessageQueue(this.sendRaw.bind(this));
  }

  /**
   * Get singleton instance.
   */
  static getInstance(gatewayUrl?: string): ConnectionManager {
    if (!ConnectionManager.instance) {
      ConnectionManager.instance = new ConnectionManager(gatewayUrl);
    }
    return ConnectionManager.instance;
  }

  /**
   * Connect to Gateway WebSocket server.
   */
  connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    // 재진입 가드: 이미 CONNECTING이면 이중 소켓을 만들지 않고
    // 진행 중인 Promise를 재사용한다.
    if (this.ws?.readyState === WebSocket.CONNECTING && this.connectPromise) {
      return this.connectPromise;
    }

    // 새 연결 시도 — 의도적 종료 플래그 해제.
    this.intentionalClose = false;

    this.connectPromise = new Promise((resolve, reject) => {
      console.log('ConnectionManager: Connecting to', this.gatewayUrl);

      try {
        // 새 소켓이므로 종료 처리 가드를 초기화한다.
        this.closeHandled = false;
        this.ws = new WebSocket(this.gatewayUrl);

        this.ws.onopen = () => {
          console.log('ConnectionManager: Connected');
          this.connectPromise = null;
          this.reconnectAttempts = 0;
          this.session.state = SessionState.CONNECTED;
          this.startHeartbeat();
          this.emit('connected', null);
          // 연결/재연결 성공 시 미전송 대기 메시지를 즉시 발송.
          this.messageQueue.flush();
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onclose = (event) => {
          this.handleClose(event.code, event.reason);
        };

        this.ws.onerror = (error) => {
          console.error('ConnectionManager: WebSocket error', error);
          this.emit('error', error);
          reject(error);
        };
      } catch (err) {
        this.connectPromise = null;
        reject(err);
      }
    });

    return this.connectPromise;
  }

  /**
   * Disconnect from Gateway.
   */
  disconnect(): void {
    // onclose 핸들러가 재연결을 시도하지 않도록 플래그를 먼저 세운다.
    this.intentionalClose = true;
    this.connectPromise = null;

    this.stopHeartbeat();
    this.stopReconnect();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.session = {
      state: SessionState.DISCONNECTED,
      account: null,
      charName: null,
    };

    this.messageQueue.clear();
    this.packetHandler.clearCache();
  }

  /**
   * Send a message to the Gateway.
   *
   * 미연결 상태면 throw하지 않고 큐에 적재한 뒤 false를 반환한다
   * (재연결 성공 시 flush로 발송). ping은 큐잉 대상이 아니다.
   *
   * @returns true = 즉시 전송됨, false = 큐잉됨(또는 ping 폐기)
   */
  send(message: ClientMessage): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      // Queue message for later if not connected
      if (message.type !== 'ping') {
        this.messageQueue.enqueue(message, message.type === 'login');
      }
      return false;
    }

    this.ws.send(JSON.stringify(message));
    return true;
  }

  /**
   * Internal send that bypasses queue.
   */
  private sendRaw(message: ClientMessage): void {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      throw new Error('Not connected to Gateway');
    }
    this.ws.send(JSON.stringify(message));
  }

  /**
   * Handle incoming WebSocket message.
   */
  private handleMessage(data: string): void {
    // 1) 파싱 실패와 디스패치 중 예외를 구분한다.
    let message: ServerMessage;
    try {
      message = JSON.parse(data);
    } catch (err) {
      console.error('ConnectionManager: Failed to parse message', err);
      return;
    }

    if (!message || typeof message.type !== 'string') {
      console.error('ConnectionManager: Malformed message (no type)', data);
      return;
    }

    console.log('ConnectionManager: Received', message.type);

    // Update last pong time when pong is received
    if (message.type === 'pong') {
      this.lastPongTime = Date.now();
    }

    // Update session state based on message
    try {
      this.updateSession(message);
    } catch (err) {
      console.error('ConnectionManager: updateSession failed', err);
    }

    const payload = (message as { payload?: unknown }).payload;

    // Emit event for listeners (emit 내부에서 리스너별로 격리됨)
    this.emit(message.type, payload);

    // GameState/GameScene 등은 networkEvents 전역 버스를 구독한다.
    // Gateway가 보낸 원본 타입 문자열을 그대로 브로드캐스트한다.
    try {
      networkEvents.emit(message.type, payload);
    } catch (err) {
      console.error('ConnectionManager: networkEvents dispatch failed', err);
    }

    // Let PacketHandler process the message
    try {
      this.packetHandler.handleServerMessage(message);
    } catch (err) {
      console.error('ConnectionManager: PacketHandler dispatch failed', err);
    }
  }

  /**
   * Update internal session state based on server messages.
   */
  private updateSession(message: ServerMessage): void {
    switch (message.type) {
      case 'connected':
        this.session.state = SessionState.CONNECTED;
        break;

      case 'login_result':
        if (message.payload?.result === 0) {
          this.session.state = SessionState.AUTHENTICATED;
        }
        break;

      case 'char_spawn':
        this.session.state = SessionState.IN_GAME;
        break;

      case 'error':
        // Check if disconnected from game server
        // (게이트웨이가 payload 없이 보낼 가능성에 대비해 방어적으로 접근)
        if (message.payload?.message?.includes('Disconnected')) {
          this.session.state = SessionState.AUTHENTICATED;
        }
        break;
    }
  }

  /**
   * 연결 종료 처리 — onclose 핸들러와 pong 타임아웃의 직접 처리 경로가 공유한다.
   *
   * 소켓 하나당 한 번만 실행된다. pong 타임아웃 때 close()가 실효성이 없어
   * 여기를 직접 호출한 뒤 뒤늦게 onclose가 오더라도 중복 emit/재연결이 나지 않는다.
   */
  private handleClose(code: number, reason: string): void {
    if (this.closeHandled) {
      return;
    }
    this.closeHandled = true;

    console.log('ConnectionManager: Disconnected', code, reason);
    this.connectPromise = null;
    this.stopHeartbeat();
    this.session.state = SessionState.DISCONNECTED;
    this.emit('disconnected', { code, reason });

    // 의도적 종료(disconnect() 호출 또는 정상 종료 코드 1000)면
    // 자동 재연결하지 않는다.
    if (!this.intentionalClose && code !== 1000) {
      this.attemptReconnect();
    }
  }

  /**
   * Attempt to reconnect with exponential backoff.
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('ConnectionManager: Max reconnect attempts reached');
      this.emit('reconnect_failed', null);
      return;
    }

    this.stopReconnect();

    const delay =
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts);
    console.log(
      `ConnectionManager: Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`
    );

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectAttempts++;
      this.connect().catch(() => {
        // Will trigger another attempt via onclose
      });
    }, delay);
  }

  private stopReconnect(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Start heartbeat ping interval.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    const now = Date.now();
    this.lastPongTime = now;
    this.lastTickTime = now;
    this.pongTimeoutHandled = false;

    // 백그라운드에 있던 탭이 다시 보이는 순간에도 타이머가 밀려 있었으므로,
    // 그동안 쌓인 시간차로 오탐하지 않도록 기준 시각을 현재로 리셋한다.
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        const resumed = Date.now();
        this.lastPongTime = resumed;
        this.lastTickTime = resumed;
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.pingTimer = window.setInterval(() => {
      const tickTime = Date.now();
      const elapsed = tickTime - this.lastTickTime;
      this.lastTickTime = tickTime;

      // 타이머가 현저히 밀린 tick은 경과 시간 자체를 신뢰할 수 없다.
      // 이 tick은 타임아웃 판정을 건너뛰고 기준 시각만 리셋한 뒤 ping만 보낸다.
      if (elapsed > this.pingInterval * ConnectionManager.TICK_DELAY_TOLERANCE) {
        this.lastPongTime = tickTime;
        this.send({ type: 'ping' });
        return;
      }

      // Check if we haven't received pong in a while
      if (tickTime - this.lastPongTime > this.pongTimeout) {
        this.handlePongTimeout();
        return;
      }

      // Send ping (미연결 시 send가 false를 반환할 뿐 throw하지 않는다)
      this.send({ type: 'ping' });
    }, this.pingInterval);
  }

  /**
   * pong 응답이 끊겼을 때의 처리.
   *
   * 경고 로그는 heartbeat 사이클당 1회만 남기고, 무엇보다 **타이머를 먼저 죽인다** —
   * ws.close()는 비동기이고 소켓이 이미 없거나 CLOSED면 no-op이라 onclose가 오지 않는데,
   * 그러면 stopHeartbeat()가 호출되지 않아 같은 분기가 pingInterval마다 영원히 반복된다.
   */
  private handlePongTimeout(): void {
    if (this.pongTimeoutHandled) {
      return;
    }
    this.pongTimeoutHandled = true;

    console.warn('ConnectionManager: Pong timeout, reconnecting...');

    // 재진입 방지: 판정 즉시 타이머를 확실히 정리한 뒤 close를 시도한다.
    this.stopHeartbeat();

    const ws = this.ws;
    if (
      ws !== null &&
      (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)
    ) {
      // 1000(정상 종료)이 아닌 앱 정의 코드로 닫아 자동 재연결이 동작하게 한다.
      ws.close(4000, 'Pong timeout');
      return;
    }

    // 소켓이 없거나 이미 CLOSING/CLOSED라 onclose를 기대할 수 없는 경우.
    // 재연결 경로가 끊기지 않도록 종료 처리를 직접 태운다
    // (handleClose가 intentionalClose 정책을 그대로 지키고, 중복 실행도 막는다).
    this.handleClose(4000, 'Pong timeout');
  }

  private stopHeartbeat(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }

    // 리스너를 남기면 소켓이 바뀌어도 계속 붙어 있게 되므로 반드시 해제한다.
    if (this.visibilityHandler !== null) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  /**
   * Event subscription.
   */
  on(event: string, callback: (data: unknown) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  /**
   * Event unsubscription.
   */
  off(event: string, callback: (data: unknown) => void): void {
    this.listeners.get(event)?.delete(callback);
  }

  /**
   * Emit event to listeners.
   */
  private emit(event: string, data: unknown): void {
    // 리스너 하나의 예외가 나머지 리스너/후속 처리를 죽이지 않도록 개별 격리.
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(data);
      } catch (err) {
        console.error(`ConnectionManager: listener error for "${event}"`, err);
      }
    });
  }

  /**
   * Get current session info.
   */
  getSession(): SessionInfo {
    return { ...this.session };
  }

  /**
   * Get session state.
   */
  getState(): SessionState {
    return this.session.state;
  }

  /**
   * Check if connected to Gateway.
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Check if authenticated (logged in).
   */
  isAuthenticated(): boolean {
    return (
      this.session.state === SessionState.AUTHENTICATED ||
      this.session.state === SessionState.IN_GAME
    );
  }

  /**
   * Check if in game (character selected).
   */
  isInGame(): boolean {
    return this.session.state === SessionState.IN_GAME;
  }

  /**
   * Get PacketHandler for game-specific message handling.
   */
  getPacketHandler(): PacketHandler {
    return this.packetHandler;
  }

  /**
   * Get MessageQueue for external access.
   */
  getMessageQueue(): MessageQueue {
    return this.messageQueue;
  }
}
